import React, { useContext, useEffect, useMemo, useState } from "react";
import { language, Language } from "../../../model/rsvp";

type I18Context = {
  language: Language;
  setLanguage: (language: Language) => void;
};

const defaultI18nContext: I18Context = {
  language: "en",
  setLanguage: () => {},
};

const i18nContext = React.createContext<I18Context>(defaultI18nContext);

i18nContext.displayName = "I18nContext";

function getLangFromBrowserSettings(): Language | null {
  try {
    if (typeof navigator === "undefined") {
      return null;
    } else {
      const lang = navigator.language;

      if (lang === "sk" || lang === "cs") {
        return "sk";
      } else if (lang === "ko") {
        return "ko";
      } else {
        return "en";
      }
    }
  } catch (err) {
    return null;
  }
}

const STORAGE_KEY = "lang";

function getLangFromLocalStorage(): Language | null {
  try {
    if (typeof window === "undefined") {
      return null;
    } else {
      return language.safeParse(localStorage.getItem(STORAGE_KEY)).data ?? null;
    }
  } catch (err) {
    return null;
  }
}

function saveLangToLocalStorage(lang: Language) {
  try {
    if (typeof window === "undefined") {
      return;
    } else {
      localStorage.setItem(STORAGE_KEY, lang);
    }
  } catch (err) {
    return;
  }
}

export const I18NProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [lang, setLang] = useState<Language>(
    getLangFromLocalStorage() ?? getLangFromBrowserSettings() ?? "en"
  );

  useEffect(() => {
    const htmlLang = document.documentElement.lang;
    if (htmlLang !== lang) {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const memoedContextValues = useMemo<I18Context>(() => {
    return {
      language: lang,
      setLanguage: (lang: Language) => {
        setLang(lang);
        saveLangToLocalStorage(lang);
      },
    };
  }, [lang, setLang]);

  return (
    <i18nContext.Provider value={memoedContextValues}>
      {children}
    </i18nContext.Provider>
  );
};

export function useI18n() {
  return useContext(i18nContext);
}
